import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { DataTable, type DataColumn } from '../../../../components/data-table/DataTable';
import { TextCell } from '../../../../components/table/TextCell';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi, evidenceReceiptsApi } from '../evidenceApi';
import type { EvidenceReceiptLine, EvidenceReceiptListItem } from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';
import { sampleReceipt } from '../utils/sampleEvidence';

const ID = 'evidence-receipt';

/**
 * GENERATES an acknowledgement receipt for a case — product calls, 08 Oct 2026.
 *
 * Who the items were received FROM and BY (employees — "by" defaults to the signed-in one), WHEN
 * (defaults to now; the server refuses a future instant), and WHICH items: any live item of the
 * case — one already on a receipt may go on another (09 Oct 2026).
 *
 * <b>SEARCH, THEN A TABLE</b> — product call, 08 Oct 2026, with the Share dialog as the picture:
 * a search picker ADDS a row, and what was added is listed in a table — type, description,
 * inventory no. — each row removable. A multi-select's chips were a row of bare
 * inventory numbers nobody could tell apart. The number is allocated on save — `EMAR-{yy}{NNNN}` — and the receipt is
 * permanent: no edit, no delete, so the tick below is the last chance to read it over.
 */
export function EvidenceReceiptModal({
  caseId,
  preset,
  onClose,
  onSaved,
}: {
  caseId: string;
  /**
   * FROM A RECEIVING EVENT — product call, 09 Oct 2026: the Received header's *Acknowledgement receipt
   * displays automatically the child items … locked*. The items are fixed (no search, no Remove); the
   * people and the time are pre-filled from the event and stay editable.
   */
  preset?: {
    itemIds: string[];
    receivedFromPersonnelId?: string | null;
    receivedByPersonnelId?: string | null;
    /** An instant. */
    receivedAt?: string;
  };
  onClose: () => void;
  onSaved: (saved: EvidenceReceiptListItem) => void;
}) {
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);
  const locked = preset !== undefined;

  const [fromId, setFromId] = useState(preset?.receivedFromPersonnelId ?? '');
  const [byId, setById] = useState(preset?.receivedByPersonnelId ?? '');
  const [receivedAt, setReceivedAt] = useState(() => toDateTimeLocal(preset?.receivedAt ? new Date(preset.receivedAt) : new Date()));
  const [itemIds, setItemIds] = useState<string[]>(preset?.itemIds ?? []);

  const [candidates, setCandidates] = useState<EvidenceReceiptLine[]>([]);
  const [loadingItems, setLoadingItems] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // A failed load names no field, so it never reaches the save path's error state.
    evidenceReceiptsApi
      .candidates(caseId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setCandidates(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingItems(false);
      });

    // THE SIGNED-IN EMPLOYEE receives by default — only while nobody was chosen.
    evidenceItemsApi
      .eligibility(controller.signal)
      .then((eligibility) => {
        const mine = eligibility.personnelId;
        if (!controller.signal.aborted && mine) setById((current) => current || mine);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [caseId]);

  const gap =
    fromId === ''
      ? 'Choose who the items were received from.'
      : byId === ''
        ? 'Choose who received them.'
        : receivedAt === ''
          ? 'Enter when they were received.'
          : itemIds.length === 0
            ? 'Choose at least one item.'
            : null;

  const canSave = gap === null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await evidenceReceiptsApi.create({
        evidenceCaseId: caseId,
        receivedFromPersonnelId: fromId,
        receivedByPersonnelId: byId,
        receivedAt: new Date(receivedAt).toISOString(),
        itemIds,
      });

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not generate the receipt.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  /*
    AN OPTION SAYS WHAT THE ITEM IS — what it is and its description lead, the numbers under it.
    A bare inventory number is a code the reader has to look up elsewhere to pick by.
  */
  const chosen = useMemo(() => new Set(itemIds), [itemIds]);
  const itemOptions = candidates
    .filter((c) => !chosen.has(c.evidenceItemId))
    .map((c) => ({
      value: c.evidenceItemId,
      label: `${c.typeName} — ${c.description}`,
      sublabel: c.inventoryNumber,
      keywords: c.inventoryNumber,
    }));

  // In the order they were added, so the row just picked lands at the bottom where the eye is.
  const candidateById = useMemo(() => new Map(candidates.map((c) => [c.evidenceItemId, c])), [candidates]);
  const added = itemIds.map((id) => candidateById.get(id)).filter((c): c is EvidenceReceiptLine => c !== undefined);

  return (
    <Modal
      open
      onClose={onClose}
      title="Generate acknowledgement receipt"
      size="3xl"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleReceipt({
            staffIds: staff.map((s) => s.value),
            itemIds: candidates.map((c) => c.evidenceItemId),
          });

          setFromId(sample.receivedFromPersonnelId);
          if (byId === '') setById(sample.receivedByPersonnelId);
          setReceivedAt(sample.receivedAt);
          // A locked receipt's items are the event's — Autogenerate never replaces them.
          if (!locked) setItemIds(sample.itemIds);
        },
        disabled: saving || loadingStaff || loadingItems,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Tooltip disabledReason={gap}>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID}-received-from-personnel-id`}
            label="Received from"
            required
            description="The employee who handed the items over."
            placeholder="Choose an employee"
            value={fromId}
            onChange={setFromId}
            options={staff}
            remote
            loading={loadingStaff}
            disabled={saving}
            error={error?.fieldError('receivedFromPersonnelId')}
          />

          <Select
            id={`${ID}-received-by-personnel-id`}
            label="Received by"
            required
            description="The employee who received them. Defaults to you."
            placeholder="Choose an employee"
            value={byId}
            onChange={setById}
            options={staff}
            remote
            loading={loadingStaff}
            disabled={saving}
            error={error?.fieldError('receivedByPersonnelId')}
          />

          <TextField
            id={`${ID}-received-at`}
            label="Received at"
            required
            type="datetime-local"
            description="When they were received. It cannot be in the future."
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            disabled={saving}
            className="font-mono tabular-nums"
            error={error?.fieldError('receivedAt')}
          />
        </div>

        <div className="flex flex-col gap-2">
          {locked ? (
            <div id={`${ID}-item-ids`} tabIndex={-1} className="flex flex-col gap-0.5">
              <p className="text-sm font-medium text-slate-700">Items</p>
              <p className="text-xs text-slate-500">
                The items of this receiving event still waiting for evaluation. They are fixed — generate a receipt from
                the Acknowledgement Receipts tab to choose items yourself.
              </p>
              {error?.fieldError('itemIds') ? <p className="text-xs text-red-700">{error.fieldError('itemIds')}</p> : null}
            </div>
          ) : (
          <Select
            id={`${ID}-item-ids`}
            label="Items"
            required
            description="Search the evidence this receipt acknowledges and pick each item to add it below. Every live item of this case is offered, including one already on another receipt."
            placeholder={
              !loadingItems && candidates.length === 0
                ? 'This case has no live evidence'
                : 'Search items by type, description or inventory no.…'
            }
            // Picking ADDS A ROW rather than holding a value — the Share dialog's shape.
            value=""
            onChange={(next) => {
              if (next && !chosen.has(next)) setItemIds((current) => [...current, next]);
            }}
            options={itemOptions}
            remote
            loading={loadingItems}
            disabled={saving || (!loadingItems && candidates.length === 0)}
            error={error?.fieldError('itemIds')}
          />
          )}

          <DataTable<EvidenceReceiptLine>
            id={`${ID}-items`}
            columns={ITEM_COLUMNS}
            rows={added}
            rowKey={(row) => row.evidenceItemId}
            sort={null}
            onToggleSort={() => undefined}
            onDelete={saving || locked ? undefined : (row) => setItemIds((current) => current.filter((id) => id !== row.evidenceItemId))}
            deleteLabel={() => 'Remove'}
            emptyMessage="No item added yet. Search above and pick the items this receipt acknowledges."
          />
        </div>

        <p className="text-xs text-slate-500">
          The receipt number is assigned when it is generated. A receipt cannot be edited or deleted afterwards.
        </p>
      </div>
    </Modal>
  );
}

// The case tables' order: what it is, then its description, then the numbers that identify it.
const ITEM_COLUMNS: DataColumn<EvidenceReceiptLine>[] = [
  { key: 'type', header: 'Type', width: 140, render: (row) => <TextCell value={row.typeName} /> },
  { key: 'description', header: 'Description', width: 280, render: (row) => <TextCell value={row.description} /> },
  { key: 'inventoryNumber', header: 'Inventory No.', width: 150, render: (row) => <TextCell value={row.inventoryNumber} mono /> },
];
